import React from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  useCancelTriggerRun,
  useResendTriggerRun,
} from "@/hooks/process-trigger/use-process-trigger";
import type { TriggerRun } from "@/types/process-trigger.types";

// Admin actions on a pending start: re-send its email, or cancel it.
const TriggerRunActions: React.FC<{ run: TriggerRun }> = ({ run }) => {
  const { t } = useTranslation();
  const cancelMutation = useCancelTriggerRun();
  const resendMutation = useResendTriggerRun();

  if (run.status !== "AWAITING_USER") return null;

  const handleCancel = () => {
    const reason = window.prompt(t("processTriggers.runs.cancelPrompt"));
    if (reason === null) return;
    cancelMutation.mutate(
      { id: run.id, reason: reason.trim() || undefined },
      {
        onSuccess: () => toast.success(t("processTriggers.runs.cancelled")),
        onError: () => toast.error(t("processTriggers.runs.actionFailed")),
      }
    );
  };

  const handleResend = () =>
    resendMutation.mutate(run.id, {
      onSuccess: () => toast.success(t("processTriggers.runs.resent")),
      onError: () => toast.error(t("processTriggers.runs.actionFailed")),
    });

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="text-xs text-darkBlue hover:underline disabled:opacity-50"
        disabled={resendMutation.isPending}
        onClick={handleResend}
      >
        {t("processTriggers.runs.resend")}
      </button>
      <button
        type="button"
        className="text-xs text-red-600 hover:underline disabled:opacity-50"
        disabled={cancelMutation.isPending}
        onClick={handleCancel}
      >
        {t("processTriggers.runs.cancel")}
      </button>
    </div>
  );
};

export default TriggerRunActions;
